import { DegreeEnum } from '../../../../../js/api/models/DegreeEnum';
import { PatchedProviderOnboarding } from '../../../../../js/api/models/PatchedProviderOnboarding';
import { SpecialtiesEnum } from '../../../../../js/api/models/SpecialtiesEnum';
import { State442Enum } from '../../../../../js/api/models/State442Enum';
import { TravelTypeEnum } from '../../../../../js/api/models/TravelTypeEnum';
import { WorkTypeEnum } from '../../../../../js/api/models/WorkTypeEnum';
import { SHIFT_TYPES } from './Rates';
import {
  BasicInfoFormData,
  RatesFormData,
  ProfessionalDetailsFormData,
  OnboardingFormData,
} from './types';

export const transformBasicInfo = (
  formData: BasicInfoFormData
): Partial<PatchedProviderOnboarding> => ({
  first_name: formData.firstName,
  last_name: formData.lastName,
  birth_date: formData.dateOfBirth
    ? (() => {
        const parts = formData.dateOfBirth.replace(/[-./]/g, '/').split('/');
        if (parts.length !== 3) return null;

        const [month, day, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      })()
    : null,
  npi: formData.npiNumber || null,
  street_address: formData.streetAddress,
  street_address2: formData.streetAddress2 || '',
  city: formData.city,
  state: formData.state as State442Enum,
  zip_code: formData.zip,
});

export const transformRates = (formData: RatesFormData): Partial<PatchedProviderOnboarding> => {
  // Create an object to hold the transformed rates
  const transformedRates = Object.values(SHIFT_TYPES).reduce<Record<string, number | null>>(
    (acc, shift) => {
      const value = formData[shift.formKey as keyof RatesFormData];
      return {
        ...acc,
        [shift.apiKey]: typeof value === 'string' ? parseFloat(value) : value,
      };
    },
    {}
  );

  return {
    ...transformedRates,
    work_type: formData.workType as WorkTypeEnum,
    travel_type: formData.travelType as TravelTypeEnum,
  };
};

export const transformProfessionalDetails = (
  formData: ProfessionalDetailsFormData
): Partial<PatchedProviderOnboarding> => {
  // Ensure degree and specialties are arrays of strings
  const degreeValues = formData.degree?.map((d) => (typeof d === 'string' ? d : d.value)) || [];
  const specialtyValues =
    formData.specialties?.map((s) => (typeof s === 'string' ? s : s.value)) || [];

  return {
    is_resident: formData.isResident,
    resident_graduation_on: formData.estimatedGraduationDate?.toISOString().split('T')[0] || null,
    has_imlc: formData.hasCompactLicense,
    degree: degreeValues as DegreeEnum[],
    specialties: specialtyValues as SpecialtiesEnum[],
    licenses: formData.licensedStates.map((state) => ({
      state: state.value as State442Enum,
      type: 'MEDICAL',
      status: 'ACTIVE',
    })),
    board_certifications: formData.boardCertifications.map((cert) => ({
      type: cert.value,
      board_eligible: cert.boardEligible,
    })),
    certifications: formData.lifeSupportCertifications.map((cert) => ({
      type: cert.value,
    })),
  };
};

export const transformFormData = (
  formData: OnboardingFormData
): Partial<PatchedProviderOnboarding> => {
  if ('firstName' in formData) {
    return transformBasicInfo(formData);
  }
  if ('shiftRateDay' in formData) {
    return transformRates(formData);
  }
  if ('licensedStates' in formData) {
    return transformProfessionalDetails(formData);
  }
  throw new Error('Unknown form data type');
};
